import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, map } from 'rxjs';

import { ResizeObserverService } from '@core/services/resize-observer.service';

import { VehicleOperationalStatusStoreModelEnum } from '@domain/single-site/single-site.enums';

import { TranslateVehicleStatusCapitalizedPipe } from '@shared/pipes/translate-enum-pipes/translate-vehicle-status-capitalized.pipe';

import { SitesTableEntitiesPerStateCellPartComponent } from '../sites-table/components/sites-table-entities-per-state-cell-part';

@Component({
  selector: 'app-sites-table-vehicles-per-state-cell',
  imports: [SitesTableEntitiesPerStateCellPartComponent, NgStyle, TranslateVehicleStatusCapitalizedPipe],
  templateUrl: './sites-table-vehicles-per-state-cell.component.html',
  styleUrl: './sites-table-vehicles-per-state-cell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SitesTableVehiclesPerStateCellComponent {
  readonly #elementRef = inject(ElementRef);
  readonly #resizeObserverService = inject(ResizeObserverService);

  public vehiclesPerState = input.required<Partial<Record<VehicleOperationalStatusStoreModelEnum, number>>>();
  public widthPx$ = toSignal(
    this.#resizeObserverService.observe$(this.#elementRef.nativeElement).pipe(
      map(() => this.#elementRef.nativeElement.getBoundingClientRect().width),
      distinctUntilChanged()
    )
  );

  public parts = computed(() => {
    const vehiclesPerState = this.vehiclesPerState();
    const widthPx = this.widthPx$();
    if (!widthPx) {
      return [];
    }
    const totalVehicles = Object.values(vehiclesPerState).reduce((acc, value) => acc + value, 0);
    const allPartsCount = Object.values(vehiclesPerState).reduce((acc, value) => {
      if (value > 0) {
        return acc + 1;
      }
      return acc;
    }, 0);

    const parts: {
      count: number;
      bgColorClass: string;
      width: number;
      status: VehicleOperationalStatusStoreModelEnum;
    }[] = [];

    const minPartWidth = 9;
    const gapSize = 2;
    const minimumRequiredSpace = allPartsCount * minPartWidth + (allPartsCount - 1) * gapSize;
    const remainingSpace = Math.max(widthPx - minimumRequiredSpace, 0);

    if (vehiclesPerState[VehicleOperationalStatusStoreModelEnum.Ready]) {
      const count = vehiclesPerState[VehicleOperationalStatusStoreModelEnum.Ready];
      parts.push({
        count,
        bgColorClass: 'bg-chargingstatus-ready-100',
        width: minPartWidth + (count / totalVehicles) * remainingSpace,
        status: VehicleOperationalStatusStoreModelEnum.Ready,
      });
    }

    if (vehiclesPerState[VehicleOperationalStatusStoreModelEnum.Charging]) {
      const count = vehiclesPerState[VehicleOperationalStatusStoreModelEnum.Charging];
      parts.push({
        count,
        bgColorClass: 'bg-chargingstatus-charging-100',
        width: minPartWidth + (count / totalVehicles) * remainingSpace,
        status: VehicleOperationalStatusStoreModelEnum.Charging,
      });
    }

    if (vehiclesPerState[VehicleOperationalStatusStoreModelEnum.Queued]) {
      const count = vehiclesPerState[VehicleOperationalStatusStoreModelEnum.Queued];
      parts.push({
        count,
        bgColorClass: 'bg-chargingstatus-queued-100',
        width: minPartWidth + (count / totalVehicles) * remainingSpace,
        status: VehicleOperationalStatusStoreModelEnum.Queued,
      });
    }

    if (vehiclesPerState[VehicleOperationalStatusStoreModelEnum.Faulted]) {
      const count = vehiclesPerState[VehicleOperationalStatusStoreModelEnum.Faulted];
      parts.push({
        count,
        bgColorClass: 'bg-chargingstatus-faulted-100',
        width: minPartWidth + (count / totalVehicles) * remainingSpace,
        status: VehicleOperationalStatusStoreModelEnum.Faulted,
      });
    }

    if (vehiclesPerState[VehicleOperationalStatusStoreModelEnum.InDepot]) {
      const count = vehiclesPerState[VehicleOperationalStatusStoreModelEnum.InDepot];
      parts.push({
        count,
        bgColorClass: 'bg-chargingstatus-indepot-100',
        width: minPartWidth + (count / totalVehicles) * remainingSpace,
        status: VehicleOperationalStatusStoreModelEnum.InDepot,
      });
    }

    if (vehiclesPerState[VehicleOperationalStatusStoreModelEnum.OnTheRoad]) {
      const count = vehiclesPerState[VehicleOperationalStatusStoreModelEnum.OnTheRoad];
      parts.push({
        count,
        bgColorClass: 'bg-chargingstatus-onroad-100',
        width: minPartWidth + (count / totalVehicles) * remainingSpace,
        status: VehicleOperationalStatusStoreModelEnum.OnTheRoad,
      });
    }

    if (vehiclesPerState[VehicleOperationalStatusStoreModelEnum.Unknown]) {
      const count = vehiclesPerState[VehicleOperationalStatusStoreModelEnum.Unknown];
      parts.push({
        count,
        bgColorClass: 'bg-chargingstatus-unknown-100',
        width: minPartWidth + (count / totalVehicles) * remainingSpace,
        status: VehicleOperationalStatusStoreModelEnum.Unknown,
      });
    }

    return parts;
  });

  public readonly VehicleOperationalStatusStoreModelEnum = VehicleOperationalStatusStoreModelEnum;
}
